import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";
import { useIsMobile } from "../../lib/useIsMobile";
import {
  TrendingUp,
  LogOut,
  PieChart,
  ChevronLeft,
  ChevronRight,
  Bell,
  Database,
  Clock,
  AlertTriangle,
  LayoutDashboard,
  Users,
  UserPlus,
  Download,
  Trash2,
  Layers,
  Network,
  Menu,
  X,
  Wallet,
  LayoutGrid,
  Crown,
  BarChart3,
  BookOpen,
  FileText,
} from "lucide-react";
import { useState, createContext, useContext, useEffect } from "react";
import { useBrandLogo } from "../../lib/branding";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Button } from "../ui/button";
import { NotificationsBell } from "./NotificationsBell";
import { can, locate, useMyRights } from "../../lib/rights";

// Create context for sidebar state
const SidebarContext = createContext();

// Associate logo when one is uploaded, else the Kinntegra "K" mark.
const BrandMark = ({ collapsed }) => {
  const logo = useBrandLogo();
  if (logo && !collapsed) return (
    <span className="flex h-10 max-w-[170px] items-center rounded-lg bg-white/95 p-1 px-2" data-testid="sidebar-brand-logo">
      <img src={logo} alt="Logo" className="max-h-full max-w-full object-contain" />
    </span>);
  return (<>
    <span className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-[rgb(var(--brand-2))] text-[rgb(var(--brand-1))] font-bold text-base tracking-tight">K</span>
    {!collapsed && <span className="text-white font-medium text-base tracking-[0.12em]">KINNTEGRA</span>}
  </>);
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    return { isCollapsed: false, toggleSidebar: () => {}, isMobile: false, mobileOpen: false, setMobileOpen: () => {} };
  }
  return context;
};

export const SidebarProvider = ({ children }) => {
  const isMobile = useIsMobile();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const toggleSidebar = () => setIsCollapsed(!isCollapsed);
  return (
    <SidebarContext.Provider value={{ isCollapsed, toggleSidebar, isMobile, mobileOpen, setMobileOpen }}>
      {children}
    </SidebarContext.Provider>
  );
};

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isCollapsed, toggleSidebar, isMobile, mobileOpen, setMobileOpen } = useSidebar();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Auto-close mobile drawer on route change
  useEffect(() => {
    if (isMobile) setMobileOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isMobile]);

  // Two distinct nav configs: full admin menu vs lean sub-broker menu.
  const adminNavItems = [
    { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard", roles: ["super_admin", "broker"] },
    { label: "Cron Jobs", icon: Clock, path: "/cronjobs", roles: ["super_admin", "broker"] },
    { label: "Research", icon: TrendingUp, path: "/mf-schemes", roles: ["super_admin", "broker"] },
    { label: "Client Wise", icon: Users, path: "/client-wise", roles: ["super_admin", "broker"] },
    { label: "Sub-Brokers", icon: UserPlus, path: "/sub-brokers/list", roles: ["super_admin", "broker"] },
    { label: "Portfolio", icon: PieChart, path: "/portfolio", roles: ["super_admin", "broker"] },
    { label: "Mismatch", icon: AlertTriangle, path: "/mismatch", roles: ["super_admin", "broker"] },
    { label: "Masters", icon: Database, path: "/masters", roles: ["super_admin", "broker"] },
    { label: "Downloads", icon: Download, path: "/downloads", roles: ["super_admin", "broker"] },
    { label: "Analytics", icon: BarChart3, path: "/analytics", roles: ["super_admin", "broker"] },
    { label: "Reset DB", icon: Trash2, path: "/reset-db", roles: ["super_admin", "broker"] },
    { label: "Data Flow", icon: Layers, path: "/data-flow", roles: ["super_admin", "broker"] },
    { label: "Leads", icon: UserPlus, path: "/leads", roles: ["super_admin", "broker", "sub_broker"] },
    // Super-admin only — IFA prospect funnel captured from the public site
    { label: "IFA Prospects", icon: Network, path: "/admin/leads", roles: ["super_admin"] },
    { label: "Commissions", icon: Wallet, path: "/commissions", roles: ["super_admin"] },
    { label: "Portfolio Strategy", icon: LayoutGrid, path: "/portfolio-strategy", roles: ["super_admin"] },
    // ── Platform Owner ONLY (multi-tenancy SaaS admin) ──
    { label: "Tenants Console", icon: Crown, path: "/platform/tenants", roles: ["platform_owner"] },
  ];

  const subBrokerNavItems = [
    { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard", roles: ["sub_broker"] },
    { label: "Client Wise Summary", icon: Users, path: "/client-wise", roles: ["sub_broker"] },
    { label: "Portfolio", icon: PieChart, path: "/portfolio", roles: ["sub_broker"] },
    { label: "Research", icon: TrendingUp, path: "/mf-schemes", roles: ["sub_broker"] },
    { label: "Leads", icon: UserPlus, path: "/leads", roles: ["sub_broker"] },
    { label: "Downloads", icon: Download, path: "/downloads", roles: ["sub_broker"] },
  ];

  const clientNavItems = [
    { label: "Portfolio", icon: PieChart, path: "/portfolio", roles: ["client"] },
  ];

  const navItems =
    user?.role === "client"
      ? clientNavItems
      : user?.role === "sub_broker"
      ? subBrokerNavItems
      : adminNavItems;

  // platform_owner sees every adminNavItem (they're a superset of super_admin)
  // PLUS their exclusive Tenants Console entry.
  const myRights = useMyRights(user);
  const filteredNavItems = navItems.filter((item) =>
    user?.role === "platform_owner"
      ? (item.roles.includes("platform_owner") || item.roles.includes("super_admin"))
      : item.roles.includes(user?.role)
  ).filter((item) => { const loc = locate(item.path); return !loc || can(myRights, loc.tab.key, "view"); });

  // ── Mobile drawer rendering ────────────────────────────────────────────────
  // On mobile, the sidebar slides over the content as a drawer. Off-screen by
  // default; opens via the hamburger button in the mobile top bar.
  if (isMobile) {
    return (
      <>
        {/* Overlay */}
        {mobileOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50"
            onClick={() => setMobileOpen(false)}
            data-testid="sidebar-overlay"
          />
        )}
        <aside
          className={cn(
            "fixed left-0 top-0 h-screen flex flex-col z-50 transition-transform duration-300 w-[260px]",
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          )}
          style={{ background: "linear-gradient(180deg, rgb(var(--brand-1b)) 0%, rgb(var(--brand-1m)) 45%, rgb(var(--brand-1)) 100%)" }}
          data-testid="sidebar"
        >
          {/* Logo + close */}
          <div className="flex items-center justify-between gap-2 px-3 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <BrandMark />
            </div>
            <button
              onClick={() => setMobileOpen(false)}
              className="text-white/70 hover:text-white p-1.5 rounded-md hover:bg-white/10"
              data-testid="sidebar-close-mobile"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-3 px-2">
            <ul className="space-y-1">
              {filteredNavItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200",
                        isActive ? "bg-[rgb(var(--brand-2)/0.15)] text-[rgb(var(--brand-2))] border-l-2 border-[rgb(var(--brand-2))]" : "text-white/65 hover:text-white hover:bg-white/[0.04]"
                      )
                    }
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              ))}
              <li>
                <button
                  onClick={() => { navigate("/settings"); setMobileOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/10"
                  data-testid="nav-settings-mobile"
                >
                  <Bell className="w-5 h-5 flex-shrink-0" />
                  <span>Settings</span>
                </button>
              </li>
            </ul>
          </nav>

          {/* Logout */}
          <div className="border-t border-white/10 p-2">
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="flex items-center gap-2 w-full px-3 py-3 text-white/70 hover:text-white hover:bg-white/10 rounded-lg text-sm font-medium"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </aside>
      </>
    );
  }

  // ── Desktop / tablet rendering (unchanged) ─────────────────────────────────
  return (
    <aside 
      className={cn(
        "fixed left-0 top-0 h-screen flex flex-col z-50 transition-all duration-300",
        isCollapsed ? "w-[70px]" : "w-[260px]"
      )}
      style={{ background: "linear-gradient(180deg, rgb(var(--brand-1b)) 0%, rgb(var(--brand-1m)) 45%, rgb(var(--brand-1)) 100%)" }}
      data-testid="sidebar"
    >
      {/* Logo with Notification - Kinntegra clickable for Settings */}
      <div className={cn(
        "flex items-center gap-2 px-3 py-3 border-b border-white/10",
        isCollapsed ? "justify-center flex-col" : "justify-between"
      )}>
        {/* Logo and Kinntegra text - clickable for Settings */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer" data-testid="kinntegra-settings-trigger">
              <BrandMark collapsed={isCollapsed} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel>Settings</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-profile">
              Profile Settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-preferences">
              Preferences
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-notifications">
              Notification Settings
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        
        {/* Notification Bell only */}
        <div className={cn(
          "flex items-center",
          isCollapsed ? "mt-2" : ""
        )}>
          <NotificationsBell />
        </div>
      </div>

      {/* Collapse Toggle Button */}
      <button
        onClick={toggleSidebar}
        className="absolute -right-3 top-16 w-6 h-6 rounded-full bg-[rgb(var(--brand-2))] shadow-md flex items-center justify-center text-[rgb(var(--brand-1))] hover:bg-[#d4b878] transition-colors z-50"
        data-testid="sidebar-toggle"
      >
        {isCollapsed ? (
          <ChevronRight className="w-4 h-4" />
        ) : (
          <ChevronLeft className="w-4 h-4" />
        )}
      </button>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        <ul className="space-y-1">
          {filteredNavItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                title={isCollapsed ? item.label : undefined}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                    isCollapsed ? "justify-center" : "",
                    isActive
                      ? "bg-[rgb(var(--brand-2)/0.15)] text-[rgb(var(--brand-2))] border-l-2 border-[rgb(var(--brand-2))]"
                      : "text-white/65 hover:text-white hover:bg-white/[0.04]"
                  )
                }
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!isCollapsed && <span>{item.label}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Logout Only */}
      <div className="border-t border-white/10 p-2">
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          title={isCollapsed ? "Logout" : undefined}
          className={cn(
            "flex items-center gap-2 w-full px-3 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg text-sm font-medium transition-all duration-200",
            isCollapsed ? "justify-center" : ""
          )}
        >
          <LogOut className="w-4 h-4" />
          {!isCollapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
